import { MOTION } from '@/components/common/motion';

/**
 * The shared table recipe — the card, the header strip, a row, a cell, the footer.
 *
 * Lifted out of the assemblies route so every table in the app lands on the same row height,
 * the same hairline and the same hover. Compose them with the pinning classes further down when
 * a table needs pinned columns; they are independent.
 *
 * The grays are hard-coded hex rather than `bg-gray-50/80` because a pinned cell inherits its
 * row's background and inheritance only works while that background is opaque — see the note
 * on the pinning constants below. Each value is what its translucent predecessor resolved to
 * over the white card.
 */

/** The card a table sits in. `overflow-hidden` keeps the rows inside the 16 px radius. */
export const TABLE_CARD = 'overflow-hidden rounded-2xl border border-gray-200 bg-white';

/** The header strip: shorter than a body row, and the only row with a full-strength border. */
export const TABLE_HEADER_ROW = 'h-10 border-b border-gray-200 bg-[#fafbfb]';

/**
 * A header cell: sentence case, and quiet enough that the values below it read first.
 *
 * Typography only — the caller adds the padding (`px-3` to line up with `TABLE_CELL`, `px-4` for
 * a roomier table) and the alignment. Baking `px-3` in here would put two padding utilities on
 * the cells that want `px-4`, and which of the two won would come down to stylesheet order.
 */
export const TABLE_HEADER_CELL = 'text-xs font-semibold text-gray-500';

/** A body row. */
export const TABLE_ROW = `h-12 border-b border-gray-100 bg-white last:border-b-0 hover:bg-[#fafbfc] ${MOTION}`;

/** A body cell holding words. */
export const TABLE_CELL = 'px-3 align-middle text-sm text-gray-700';

/** A body cell holding a measurement: right-aligned, with digits that line up between rows. */
export const TABLE_CELL_NUM = `${TABLE_CELL} text-right tabular-nums`;

/** The strip under the last row — pagination on the left, the count on the right. */
export const TABLE_FOOTER = 'flex items-center justify-between border-t border-gray-200 bg-[#fafbfb] px-4 py-2';

/**
 * Shared classes for tables that pin their first and last column while the middle scrolls.
 *
 * A pinned cell has to be opaque, or the columns sliding underneath show through it. The
 * trick that keeps it opaque *without* freezing its colour is `bg-inherit`: the row still
 * owns the stripe and the hover, and the cell simply takes whatever the row currently
 * computes to. Highlights that live on an inner element — a filter-active button, an
 * amber pending edit — paint on top of that and are unaffected.
 *
 * `bg-inherit` only works while the row's own background is opaque, which is why the
 * backgrounds below are solid rather than the translucent tints a table would otherwise
 * reach for. Each one is the exact colour its translucent predecessor resolved to over the
 * white card, so pinning changed nothing visually.
 */

/** Header row background — the opaque equivalent of `bg-gray-50/90` over white. */
export const TABLE_HEADER_BG = 'bg-[#fafbfb]';

/** Even (zero-indexed) body rows. */
export const TABLE_ROW_BG_EVEN = 'bg-white';

/** Odd body rows — the opaque equivalent of `bg-gray-50/50` over white. */
export const TABLE_ROW_BG_ODD = 'bg-[#fcfcfd]';

/** Row hover — the opaque equivalent of `bg-gray-50/80` over white. */
export const TABLE_ROW_BG_HOVER = 'hover:bg-[#fafbfc]';

/**
 * Column pinned to the left edge. The inset shadow draws the seam on the cell's right, so
 * the edge stays readable once content scrolls beneath it.
 */
export const STICKY_COL_LEFT = 'sticky left-0 bg-inherit shadow-[inset_-1px_0_0_rgb(229_231_235)]';

/** Column pinned to the right edge, with the seam mirrored onto its left. */
export const STICKY_COL_RIGHT = 'sticky right-0 bg-inherit shadow-[inset_1px_0_0_rgb(229_231_235)]';

/** Pinned body cells ride above the scrolling ones. */
export const STICKY_COL_BODY_Z = 'z-10';

/** Pinned header cells ride above the pinned body cells in turn. */
export const STICKY_COL_HEADER_Z = 'z-20';

/** Everything a pinned left header cell needs. */
export const STICKY_LEFT_HEADER = `${STICKY_COL_LEFT} ${STICKY_COL_HEADER_Z}`;

/** Everything a pinned right header cell needs. */
export const STICKY_RIGHT_HEADER = `${STICKY_COL_RIGHT} ${STICKY_COL_HEADER_Z}`;

/** Everything a pinned left body cell needs. */
export const STICKY_LEFT_CELL = `${STICKY_COL_LEFT} ${STICKY_COL_BODY_Z}`;

/** Everything a pinned right body cell needs. */
export const STICKY_RIGHT_CELL = `${STICKY_COL_RIGHT} ${STICKY_COL_BODY_Z}`;

/**
 * The same two columns while something is actually scrolled underneath them.
 *
 * The seam is always drawn; the soft drop shadow beside it is added only on the edge that has
 * rows sliding under it, so a table that fits its container shows no shadow at all. Pair them
 * with a scroll-position flag — see `scrolledEdges` in the assemblies route.
 */
export const STICKY_COL_LEFT_ELEVATED =
  'sticky left-0 bg-inherit shadow-[inset_-1px_0_0_rgb(229_231_235),8px_0_10px_-8px_#0f172a33]';

/** The right-pinned column with the drop shadow mirrored onto its left. */
export const STICKY_COL_RIGHT_ELEVATED =
  'sticky right-0 bg-inherit shadow-[inset_1px_0_0_rgb(229_231_235),-8px_0_10px_-8px_#0f172a33]';

/** Everything a pinned left header cell needs while content is scrolled under it. */
export const STICKY_LEFT_HEADER_ELEVATED = `${STICKY_COL_LEFT_ELEVATED} ${STICKY_COL_HEADER_Z}`;

/** Everything a pinned right header cell needs while content is scrolled under it. */
export const STICKY_RIGHT_HEADER_ELEVATED = `${STICKY_COL_RIGHT_ELEVATED} ${STICKY_COL_HEADER_Z}`;

/** Everything a pinned left body cell needs while content is scrolled under it. */
export const STICKY_LEFT_CELL_ELEVATED = `${STICKY_COL_LEFT_ELEVATED} ${STICKY_COL_BODY_Z}`;

/** Everything a pinned right body cell needs while content is scrolled under it. */
export const STICKY_RIGHT_CELL_ELEVATED = `${STICKY_COL_RIGHT_ELEVATED} ${STICKY_COL_BODY_Z}`;
